import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCourse } from 'services/course/getCourse';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField
} from '@mui/material';
import {
  ArrowRightOutlined,
  EditOutlined,
  LineChartOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  AppstoreAddOutlined,
  MoreOutlined,
  UserAddOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import CreateCourseDialogue from 'components/openDialog/CreateCourse';
import EditCourseDialogue from 'components/openDialog/EditCourse';
import AssignStudentsDialogue from 'components/openDialog/AssignStudents';
import { getstudents } from 'services/student/getStudents';
import { AssignStudentCourse } from 'services/course/AssignStudentCourse';
import toast from 'react-hot-toast';
import { AirOutlined } from '@mui/icons-material';

function Instructor() {
  const navigate = useNavigate();
  const coachId = localStorage.getItem('userid');
  const [openCreateCourse, setOpenCreateCourse] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [currentCourseId, setCurrentCourseId] = useState(null);

  const {
    data: courseData,
    isLoading: isCourseLoading,
    isError: isCourseError
  } = useQuery({
    queryKey: ['allcourses'],
    queryFn: () => getCourse({ coachid: Number(coachId) }),
    retryOnMount: true
  });

  const courseList = courseData?.response || [];

  const difficulty = courseList.difficulty?.toLowerCase();

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'success'; // green
      case 'intermediate':
        return 'warning'; // orange/yellow
      case 'advanced':
        return 'error'; // red
      default:
        return 'default'; // grey
    }
  };

  const {
    data: studentData,
    isLoading: isStudentsLoading,
    isError: isStudentsError
  } = useQuery({
    queryKey: ['students'],
    queryFn: () => getstudents({ coachId: Number(coachId) })
  });

  const studentList = studentData?.data || [];

  const handleMenuOpen = (event, courseId) => {
    setAnchorEl(event.currentTarget);
    setCurrentCourseId(courseId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    if (!openAssignDialog) {
      setCurrentCourseId(null);
    }
  };

  const handleAssignStudent = (courseId) => {
    setCurrentCourseId(courseId);
    setOpenAssignDialog(true);
    setAnchorEl(null); // Only close the menu, don't reset currentCourseId
  };

  const handleViewAssignedStudents = () => {
    navigate(`/assigned-students/${currentCourseId}`);
    setAnchorEl(null);
  };

  const assignMutation = useMutation({
    mutationFn: AssignStudentCourse,
    onSuccess: (response) => {
      toast.success(response?.data?.message);
      setOpenAssignDialog(false);
      setSelectedStudents([]);
    },
    onError: (error) => {
      console.error('Assignment failed:', error);
      toast.error(error?.response?.data?.message);
    }
  });

  const handleAssignSubmit = () => {
    console.log('Assigning students:', selectedStudents, 'to course:', currentCourseId);
    if (!currentCourseId || selectedStudents.length === 0) return;

    selectedStudents.forEach((student) => {
      console.log('Calling API for studentId:', student.user_id);
      assignMutation.mutate({
        courseId: currentCourseId,
        studentId: student.user_id
      });
    });

    // Close dialog and reset state
    setOpenAssignDialog(false);
    setSelectedStudents([]);
    setCurrentCourseId(null); // Reset currentCourseId after submission
  };

  // const handleAssignSubmit = () => {
  //   if (!currentCourseId || selectedStudents.length === 0) return;

  //   selectedStudents.forEach((student) => {
  //     assignMutation.mutate({
  //       courseId: currentCourseId,
  //       studentId: student.user_id
  //     });
  //   });

  return (
    <>
      <Box sx={{ minHeight: 'auto' }}>
        {' '}
        {/* Scrollable parent */}
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            px: 2,
            position: 'sticky',
            top: 52,
            zIndex: 1100,
            backgroundColor: 'background.paper',
            py: 2
          }}
        >
          <Typography variant="h4" fontWeight={600} color="text.primary">
            My Courses
          </Typography>
          <Box>
            <Button sx={{ mr: 3 }} variant="contained" onClick={() => navigate('/library')}>
              View Library
            </Button>
            <Button variant="contained" onClick={() => setOpenCreateCourse(true)}>
              Create Course
            </Button>
          </Box>
        </Box>
        {/* Create Dialog */}
        <CreateCourseDialogue open={openCreateCourse} onClose={() => setOpenCreateCourse(false)} title="Create Course" />
        {/* Edit Dialog */}
        <EditCourseDialogue
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          courseData={selectedCourse}
          title="Edit Course"
        />
        {/* Assign Student Dialog */}
        <AssignStudentsDialogue
          open={openAssignDialog}
          onClose={() => setOpenAssignDialog(false)}
          studentList={studentList}
          selectedStudents={selectedStudents}
          setSelectedStudents={setSelectedStudents}
          onAssign={handleAssignSubmit}
        />
        {/* Body */}
        {isCourseLoading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Loading Courses...
            </Typography>
          </Box>
        ) : isCourseError ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="error.main">
              Failed to fetch courses. Please try again.
            </Typography>
          </Box>
        ) : courseList.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2, mx: 2 }}>
            <Typography variant="h4" fontWeight={600} color="text.secondary" gutterBottom>
              No Courses Available
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Click "Create Course" to start adding new yoga courses.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ px: 2 }}>
            {courseList.map((course) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={course.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3, bgcolor: 'background.paper' }}>
                    {/* Title and Category Chip */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{
                          textTransform: 'capitalize',
                          color: 'primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {course.courseName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={course.category}
                          size="small"
                          sx={{
                            bgcolor: '#1A237E	',
                            color: 'primary.contrastText',
                            fontWeight: 500,
                            borderRadius: 1
                          }}
                        />
                        <IconButton onClick={(e) => handleMenuOpen(e, course.courseId)} size="small">
                          <MoreOutlined />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Description */}
                    {course.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 3,
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {course.description}
                      </Typography>
                    )}

                    {/* Divider */}
                    <Divider sx={{ mb: 3, borderColor: 'grey.200' }} />

                    {/* Info Grid */}
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      {/* Asanas */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LineChartOutlined style={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontWeight: 500 }}>
                          Asanas
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {course.asanaCount}
                        </Typography>
                      </Box>


                       {/* Pranaymmas */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AirOutlined style={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontWeight: 500 }}>
                          Pranayama
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {course.pranayamaCount}
                        </Typography>
                      </Box>

                      {/* Difficulty */}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <InfoCircleOutlined style={{ fontSize: 20, color: 'green' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontWeight: 500 }}>
                          Difficulty
                        </Typography>
                        <Chip label={course.difficulty} size="small" color={getDifficultyColor(course.difficulty?.toLowerCase())} />
                      </Box>

                      {/* Sequence Type */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AppstoreAddOutlined style={{ fontSize: 20, color: 'warning.main' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontWeight: 500 }}>
                          Sequence
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {course.sequenceType || 'N/A'}
                        </Typography>
                      </Box>

                      {/* Created Date */}
                      {course.createdAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CalendarOutlined style={{ fontSize: 20, color: 'info.main' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontWeight: 500 }}>
                            Created On
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {new Date(course.createdAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0,
                      gap: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      flexDirection: 'row', // 👈 horizontal
                      justifyContent: 'space-between' // optional
                    }}
                  >
                    <Button
                      size="medium"
                      variant="outlined"
                      startIcon={<UsergroupAddOutlined />}
                      fullWidth
                      onClick={() => {
                        setSelectedCourse(course);
                        handleAssignStudent(course.courseId);
                      }}
                    >
                      Assign 
                    </Button>
                    <Button
                      size="medium"
                      variant="contained"
                      endIcon={<ArrowRightOutlined />}
                      fullWidth
                      onClick={() => navigate(`/view-asana/${course.courseId}`)}
                      sx={{
                        textTransform: 'none',
                        bgcolor: 'primary.main',
                        margin: '0 !important',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      Manage
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            // onClick={() => {
            //   setSelectedCourse(selectedCourse);
            //   setOpenEditDialog(true);
            //   handleMenuClose();
            // }}
            onClick={() => {
              const course = courseList.find((c) => c.courseId === currentCourseId);
              setSelectedCourse(course);
              setOpenEditDialog(true);
              handleMenuClose();
            }}
          >
            <EditOutlined style={{ marginRight: 8 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleViewAssignedStudents}>
            <UserAddOutlined style={{ marginRight: 8 }} />
            View Assigned Student
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
}

export default Instructor;
